import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/utils/apiClient";
import { useAuth } from "@/state/auth";

export interface PromptSettings {
  adminInstructions: string;
  referenceData: string;
  enabled: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface PromptSettingsResponse {
  config: PromptSettings;
  defaults: {
    referenceData: string;
  };
  limits: {
    adminInstructionsMaxBytes: number;
    referenceDataMaxBytes: number;
  };
}

export const usePromptSettings = () => {
  const profile = useAuth((state) => state.profile);
  return useQuery({
    queryKey: ["admin", "prompt-settings"],
    queryFn: async () =>
      apiRequest<PromptSettingsResponse>("/api/admin/system-prompt"),
    staleTime: 60_000,
    enabled: Boolean(profile),
  });
};

export const useUpdatePromptSettings = () => {
  const profile = useAuth((state) => state.profile);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      adminInstructions: string;
      referenceData: string;
      enabled: boolean;
    }) => {
      const body = {
        admin_instructions: payload.adminInstructions,
        reference_data: payload.referenceData,
        enabled: payload.enabled,
      };
      const { config } = await apiRequest<{ config: PromptSettings }>(
        "/api/admin/system-prompt",
        { method: "PUT", body: JSON.stringify(body) }
      );
      return config;
    },
    onSuccess: () => {
      if (profile) {
        queryClient.invalidateQueries({ queryKey: ["admin", "prompt-settings"] });
      }
    },
  });
};
